import React from 'react';
import { CheckCircle2, ShieldCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../firebase';
import {
  promoteLandGateLayer,
  unpromoteLandGateLayer,
} from '../../lib/siteLayers/landGatePromotion';
import type {
  LandGateConstraintType,
  LandGateInfrastructureType,
  LandGatePromotedFact,
  SiteLayer,
} from '../../types/site';
import { Button } from '../ui/button';

export interface LandGatePromotionControlsProps {
  layer: SiteLayer;
  onChange: (layer: SiteLayer) => void;
}

type FactKind = LandGatePromotedFact['kind'];

function attribute(layer: SiteLayer, names: readonly string[]): string {
  const entries = Object.entries(layer.attributes ?? {});
  for (const name of names) {
    const found = entries.find(([key]) => key.toLowerCase() === name);
    if (found?.[1] != null) return String(found[1]);
  }
  return '';
}

export const LandGatePromotionControls: React.FC<LandGatePromotionControlsProps> = ({ layer, onChange }) => {
  const [kind, setKind] = React.useState<FactKind>('parcel');
  const [primary, setPrimary] = React.useState('');
  const [secondary, setSecondary] = React.useState('');
  const [tertiary, setTertiary] = React.useState('');
  const [constraintType, setConstraintType] = React.useState<LandGateConstraintType>('other');
  const [infrastructureType, setInfrastructureType] = React.useState<LandGateInfrastructureType>('other');
  const [present, setPresent] = React.useState(true);

  React.useEffect(() => {
    setKind('parcel');
    setPrimary(attribute(layer, ['parcel_id', 'parcelid', 'apn']));
    setSecondary(attribute(layer, ['acreage', 'acres']));
    setTertiary(attribute(layer, ['owner', 'owner_name']));
    setConstraintType('other');
    setInfrastructureType('other');
    setPresent(true);
  }, [layer.id]);

  if (layer.source !== 'landgate-import') return null;
  const review = layer.evidence?.promotion;
  const promoted = review?.state === 'promoted';

  const switchKind = (next: FactKind) => {
    setKind(next);
    if (next === 'parcel') {
      setPrimary(attribute(layer, ['parcel_id', 'parcelid', 'apn']));
      setSecondary(attribute(layer, ['acreage', 'acres']));
      setTertiary(attribute(layer, ['owner', 'owner_name']));
    } else if (next === 'zoning') {
      setPrimary(attribute(layer, ['zoning', 'zoning_code']));
      setSecondary(attribute(layer, ['jurisdiction']));
      setTertiary('');
    } else {
      setPrimary(layer.name);
      setSecondary('');
      setTertiary('');
    }
  };

  const buildFact = (): LandGatePromotedFact => {
    if (kind === 'parcel') {
      return {
        kind,
        ...(primary.trim() ? { parcelId: primary } : {}),
        ...(secondary.trim() ? { acreage: Number(secondary) } : {}),
        ...(tertiary.trim() ? { owner: tertiary } : {}),
      };
    }
    if (kind === 'zoning') return { kind, zoningCode: primary, ...(secondary.trim() ? { jurisdiction: secondary } : {}) };
    if (kind === 'constraint') return { kind, constraintType, present, ...(primary.trim() ? { detail: primary } : {}) };
    return { kind, infrastructureType, label: primary };
  };

  const reviewer = () => {
    const user = auth.currentUser;
    if (!user?.uid) throw new Error('Sign in before reviewing LandGate evidence');
    return { uid: user.uid, email: user.email };
  };

  const promote = () => {
    try {
      onChange(promoteLandGateLayer(layer, { facts: [buildFact()] }, reviewer()));
      toast.success('LandGate fact staged as reviewed screening evidence');
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const unpromote = () => {
    try {
      onChange(unpromoteLandGateLayer(layer, reviewer()));
      toast.success('LandGate layer staged as map-only');
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <div className="border border-border-subtle bg-state-hover p-2 space-y-2" data-testid="landgate-promotion-controls">
      <div className="flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-primary" aria-hidden />
        <span className="type-section text-text-primary">LandGate evidence review</span>
      </div>
      <p className="type-body text-text-muted">
        Map-only by default. Promotion creates reviewed screening evidence; it does not make the vendor record legal or professional site truth.
      </p>
      {promoted ? (
        <>
          <div className="flex items-center gap-1 type-caption text-success" data-testid="landgate-promoted-status">
            <CheckCircle2 className="w-3 h-3" aria-hidden />
            Promoted · {review.facts.map(fact => fact.kind).join(', ')}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full h-7 rounded-md type-section"
            onClick={unpromote}
            data-testid="landgate-unpromote"
          >
            <Undo2 className="w-3 h-3 mr-1" /> Return to map-only
          </Button>
        </>
      ) : (
        <>
          <select
            value={kind}
            onChange={event => switchKind(event.target.value as FactKind)}
            className="w-full type-body px-2 py-1 border border-border bg-surface-1 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
            aria-label="LandGate fact type"
            data-testid="landgate-fact-kind"
          >
            <option value="parcel">Parcel fact</option>
            <option value="zoning">Zoning fact</option>
            <option value="constraint">Constraint fact</option>
            <option value="existing-infrastructure">Existing infrastructure</option>
          </select>

          {kind === 'constraint' && (
            <div className="flex gap-1">
              <select
                value={constraintType}
                onChange={event => setConstraintType(event.target.value as LandGateConstraintType)}
                className="flex-1 type-body px-2 py-1 border border-border bg-surface-1 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
                aria-label="Constraint type"
              >
                <option value="floodplain">Floodplain</option>
                <option value="wetland">Wetland</option>
                <option value="protected-area">Protected area</option>
                <option value="setback">Setback</option>
                <option value="slope">Slope</option>
                <option value="other">Other</option>
              </select>
              <label className="flex items-center gap-1 type-caption px-1">
                <input
                  type="checkbox"
                  checked={present}
                  onChange={event => setPresent(event.target.checked)}
                  className="accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
                /> Present
              </label>
            </div>
          )}

          {kind === 'existing-infrastructure' && (
            <select
              value={infrastructureType}
              onChange={event => setInfrastructureType(event.target.value as LandGateInfrastructureType)}
              className="w-full type-body px-2 py-1 border border-border bg-surface-1 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
              aria-label="Infrastructure type"
            >
              <option value="electric">Electric</option>
              <option value="pipeline">Pipeline</option>
              <option value="fiber">Fiber</option>
              <option value="transport">Transport</option>
              <option value="water">Water</option>
              <option value="other">Other</option>
            </select>
          )}

          <input
            value={primary}
            onChange={event => setPrimary(event.target.value)}
            placeholder={kind === 'parcel' ? 'Parcel ID' : kind === 'zoning' ? 'Zoning code' : kind === 'constraint' ? 'Constraint detail' : 'Infrastructure label'}
            className="w-full type-body px-2 py-1 border border-border bg-surface-1 text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
            data-testid="landgate-primary-value"
          />
          {kind === 'parcel' && (
            <>
              <input value={secondary} onChange={event => setSecondary(event.target.value)} type="number" min="0" placeholder="Acreage (optional)" className="w-full type-body px-2 py-1 border border-border bg-surface-1 text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1" />
              <input value={tertiary} onChange={event => setTertiary(event.target.value)} placeholder="Owner (optional)" className="w-full type-body px-2 py-1 border border-border bg-surface-1 text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1" />
            </>
          )}
          {kind === 'zoning' && (
            <input value={secondary} onChange={event => setSecondary(event.target.value)} placeholder="Jurisdiction (optional)" className="w-full type-body px-2 py-1 border border-border bg-surface-1 text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1" />
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full h-7 rounded-md type-section"
            onClick={promote}
            data-testid="landgate-promote"
          >
            Promote to screening
          </Button>
        </>
      )}
    </div>
  );
};
